import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaSignOutAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';

const Navbar = ({ setIsLoggedIn }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('email');
    localStorage.removeItem('isSubscribed');
    localStorage.removeItem('fertilityNews');
    setIsLoggedIn(false);
    navigate('/');
  };

  const isActive = (path) => {
    if (path === '/newsletter') {
      return (
        location.pathname === '/newsletter' ||
        location.pathname === '/newsletter/fertility-treatments' ||
        location.pathname === '/newsletter/nutrition-tips' ||
        location.pathname === '/newsletter/menstrual-cycle'
      );
    }
    return location.pathname === path;
  };

  const useFor = localStorage.getItem('useFor');

  return (
    <motion.nav 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={styles.navbar}
    >
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Link to="/dashboard" style={styles.logo}>
          <img src="/logo192.png" alt="UmbraCare Logo" style={styles.logoImg} />
        </Link>
      </motion.div>
      <div style={styles.navLinks}>
        <motion.div whileHover={{ scale: 1.05 }}>
          <Link to="/dashboard" style={{ ...styles.navLink, ...(isActive('/dashboard') ? styles.activeLink : {}) }}>Home</Link>
        </motion.div>
        {useFor === 'pregnancy' && (
          <motion.div whileHover={{ scale: 1.05 }}>
            <Link to="/pregnancy-postpartum-tracker" style={{ ...styles.navLink, ...(isActive('/pregnancy-postpartum-tracker') ? styles.activeLink : {}) }}>Pregnancy & Postpartum Tracker</Link>
          </motion.div>
        )}
        {useFor === 'period' && (
          <motion.div whileHover={{ scale: 1.05 }}>
            <Link to="/period-tracker" style={{ ...styles.navLink, ...(isActive('/period-tracker') ? styles.activeLink : {}) }}>Period Tracker</Link>
          </motion.div>
        )}
        {useFor === 'pregnancy' && (
          <motion.div whileHover={{ scale: 1.05 }}>
            <Link to="/ivf-tracker" style={{ ...styles.navLink, ...(isActive('/ivf-tracker') ? styles.activeLink : {}) }}>IVF Tracker</Link>
          </motion.div>
        )}
        <motion.div whileHover={{ scale: 1.05 }}>
          <Link to="/doctor-info" style={{ ...styles.navLink, ...(isActive('/doctor-info') ? styles.activeLink : {}) }}>Doctor Info</Link>
        </motion.div>
        <motion.div whileHover={{ scale: 1.05 }}>
          <Link to="/notifications" style={{ ...styles.navLink, ...(isActive('/notifications') ? styles.activeLink : {}) }}>Notifications</Link>
        </motion.div>
        <motion.div whileHover={{ scale: 1.05 }}>
          <Link to="/profile" style={{ ...styles.navLink, ...(isActive('/profile') ? styles.activeLink : {}) }}>Profile</Link>
        </motion.div>
        <motion.div whileHover={{ scale: 1.05 }}>
          <Link to="/about" style={{ ...styles.navLink, ...(isActive('/about') ? styles.activeLink : {}) }}>About Us</Link>
        </motion.div>
        <motion.div whileHover={{ scale: 1.05 }}>
          <Link to="/newsletter" style={{ ...styles.navLink, ...(isActive('/newsletter') ? styles.activeLink : {}) }}>Newsletter</Link>
        </motion.div>
        <motion.div whileHover={{ scale: 1.05 }}>
          <Link to="/feedback" style={{ ...styles.navLink, ...(isActive('/feedback') ? styles.activeLink : {}) }}>Feedback</Link>
        </motion.div>
        <motion.button 
          whileHover={{ scale: 1.05, backgroundColor: '#fff', color: '#b15870' }}
          whileTap={{ scale: 0.95 }}
          onClick={handleLogout} 
          style={styles.logoutButton}
        >
          <FaSignOutAlt style={{ marginRight: '5px' }} /> Logout
        </motion.button>
      </div>
    </motion.nav>
  );
};

const styles = {
  navbar: { 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    padding: '10px 30px', 
    backgroundColor: '#b15870', 
    color: '#fff', 
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)', 
    position: 'fixed', 
    top: 0, 
    width: '100%', 
    zIndex: 1000, 
    boxSizing: 'border-box',
    height: '90px',
    backdropFilter: 'blur(10px)',
  },
  logo: { 
    fontSize: '24px', 
    fontWeight: 'bold', 
    color: '#fff', 
    textDecoration: 'none', 
    display: 'flex', 
    alignItems: 'center' 
  },
  logoImg: { 
    width: '70px',
    height: '70px',
    marginRight: '50px', // Increased back to 50px to create more space between logo and first tab
    boxShadow: '0 0 10px rgba(255, 255, 255, 0.8)',
    borderRadius: '50%',
    transition: 'transform 0.3s ease',
  },
  navLinks: { 
    display: 'flex', 
    justifyContent: 'flex-end', // Keep tabs shifted to the right
    alignItems: 'center', 
    flex: 1,
    marginLeft: '0', // No left margin to keep tabs right-aligned
    gap: '17px', // Increased from 5px to 8px for more space between tabs
  },
  navLink: { 
    color: '#fff', 
    textDecoration: 'none', 
    fontSize: '16px', 
    transition: 'all 0.3s ease', 
    padding: '8px 15px', // Reverted padding to original 15px for more space around text
    borderRadius: '8px',
    whiteSpace: 'nowrap',
    position: 'relative',
    overflow: 'hidden',
  },
  activeLink: { 
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#fff',
    boxShadow: '0 0 10px rgba(255, 255, 255, 0.3)',
  },
  logoutButton: { 
    display: 'flex', 
    alignItems: 'center', 
    backgroundColor: 'transparent', 
    color: '#fff', 
    padding: '8px 15px', // Reverted to original padding for consistency
    border: '2px solid #fff',
    borderRadius: '8px', 
    cursor: 'pointer', 
    fontSize: '16px', 
    transition: 'all 0.3s ease',
    marginLeft: '15px', // Increased from 10px to 15px for more space before logout
    fontWeight: 'bold',
  },
};

export default Navbar;